import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Category, SortDirection, FormStatus } from '@/types';
import { 
    getCategoryDoc, 
    getCategoryDocs, 
    addCategoryDoc, 
    setCategoryDoc, 
    deleteCategoryDoc,
    setCustomInputs,
    restoreCategoryDoc
} from '@/services/firebaseApi';
import { logger } from '@/utils/secureLogger';
import { trackCreate, trackDelete, trackEdit, trackRestore } from '@/services/analytics';

export enum CategoriesSortBy {
    ID = 'categoryId',
    NAME = 'name',
}

interface UseCategoriesProps {
    storageId?: string;
    categoryId?: string;
}

const useCategories = ({ storageId, categoryId }: UseCategoriesProps) => {
    const queryClient = useQueryClient();
    
    // Filter states
    const [sortBy, setSortBy] = useState(CategoriesSortBy.NAME);
    const [sortDirection, setSortDirection] = useState(SortDirection.ASCENDING);
    const [filterQuery, setFilterQuery] = useState<string>('');
    const [displayDeleted, setDisplayDeleted] = useState<boolean>(false);

    // All categories query
    const {
        data: allCategories,
        isLoading: categoriesLoading,
        error: categoriesError,
        refetch: refetchCategories
    } = useQuery({
        queryKey: ['categories', storageId, sortBy, sortDirection, displayDeleted],
        queryFn: () => getCategoryDocs({ 
            storageId: storageId!,
            sortBy,
            sortDirection,
            includeDeleted: displayDeleted
        }),
        enabled: !!storageId && !categoryId,
        staleTime: 5 * 60 * 1000, // Categories don't change often
        gcTime: 15 * 60 * 1000, // Keep in cache longer
    });

    // Single category query
    const {
        data: category,
        isLoading: categoryLoading,
        error: categoryError,
        refetch: refetchCategory
    } = useQuery({
        queryKey: ['category', storageId, categoryId],
        queryFn: () => getCategoryDoc(storageId!, categoryId!),
        enabled: !!(storageId && categoryId),
        staleTime: 5 * 60 * 1000,
    });

    // Apply client-side filtering and sorting
    const filteredCategories = useMemo(() => {
        if (!allCategories) return [];
        
        let filtered = [...allCategories];

        // Apply search filter
        if (filterQuery) {
            const lower = filterQuery.toLowerCase();
            filtered = filtered.filter((category) =>
                category.categoryId.toString().includes(lower) ||
                category.name.toLowerCase().includes(lower) ||
                (category.description && category.description.toLowerCase().includes(lower))
            );
        }

        // Note: displayDeleted filtering is now handled server-side via the includeDeleted parameter

        return filtered;
    }, [allCategories, filterQuery]);

    // Mutations
    const createCategoryMutation = useMutation({
        mutationFn: addCategoryDoc,
        onSuccess: (result, variables) => {
            trackCreate('category');
            logger.info('Category created successfully');
            
            // Invalidate all category queries for this storage (all sort/filter combinations)
            queryClient.invalidateQueries({ queryKey: ['categories', variables.ids.storageId] });
            queryClient.invalidateQueries({ queryKey: ['storage', variables.ids.storageId] });
            queryClient.invalidateQueries({ queryKey: ['storages'] });

            // Remove storage list cache so it will be refetched
            queryClient.removeQueries({
                queryKey: ['storage', variables.ids.storageId],
                exact: true,
            });
        },
        onError: (error) => {
            logger.error('Failed to create category:', error);
        }
    });

    const updateCategoryMutation = useMutation({
        mutationFn: setCategoryDoc,
        onSuccess: (result, variables) => {
            trackEdit('category');
            logger.info('Category updated successfully:', variables.ids.categoryId);
            queryClient.invalidateQueries({ queryKey: ['categories', variables.ids.storageId] });
            queryClient.invalidateQueries({ queryKey: ['category', variables.ids.storageId, variables.ids.categoryId] });
        },
        onError: (error) => {
            logger.error('Failed to update category:', error);
        }
    });

    const deleteCategoryMutation = useMutation({
        mutationFn: deleteCategoryDoc,
        onSuccess: (result, variables) => {
            trackDelete('category');
            logger.info('Category deleted (soft) successfully:', variables.categoryId);
            queryClient.invalidateQueries({ queryKey: ['categories', variables.storageId] });
            queryClient.invalidateQueries({ queryKey: ['category', variables.storageId, variables.categoryId] });
        },
        onError: (error) => {
            logger.error('Failed to delete category:', error);
        }
    });

    const restoreCategoryMutation = useMutation({
        mutationFn: restoreCategoryDoc,
        onSuccess: (result, variables) => {
            trackRestore('category');
            logger.info('Category restored successfully:', variables.categoryId);
            queryClient.invalidateQueries({ queryKey: ['categories', variables.storageId] });
            queryClient.invalidateQueries({ queryKey: ['category', variables.storageId, variables.categoryId] });
        },
        onError: (error) => {
            logger.error('Failed to restore category:', error);
        }
    });

    const setCustomInputsMutation = useMutation({
        mutationFn: setCustomInputs,
        onSuccess: (result, variables) => {
            trackEdit('custom_input');
            logger.info('Custom inputs updated successfully:', variables.ids.categoryId);
            queryClient.invalidateQueries({ queryKey: ['categories', variables.ids.storageId] });
            queryClient.invalidateQueries({ queryKey: ['category', variables.ids.storageId, variables.ids.categoryId] });
        },
        onError: (error) => {
            logger.error('Failed to update custom inputs:', error);
        }
    });

    // Helper function to determine status
    const getStatus = (loading: boolean, error: any): FormStatus => {
        if (loading) return FormStatus.LOADING;
        if (error) return FormStatus.ERROR;
        return FormStatus.SUCCESS;
    };

    // Backward compatible functions
    const reloadCategories = () => {
        if (categoryId) {
            refetchCategory();
        } else {
            refetchCategories();
        }
    };

    // Function to get category by doc ID
    const findCategory = useMemo(() => {
        return (id: string): Category | undefined => {
            return allCategories?.find((cat: Category) => cat && cat.id === id);
        };
    }, [allCategories]);

    // Function to get category name string by doc ID
    const getCategoryNameString = useMemo(() => {
        return (id: string): string | undefined => {
            const category = findCategory(id);
            return category ? `${category.categoryId}: ${category.name}` : undefined;
        };
    }, [findCategory]);

    // Function to get multiple category names
    const getCategoryNamesString = useMemo((categories: Category[] | undefined = allCategories) => {
        return (ids: string[]): string => {
            const names = ids
                .map(id => {
                    const category = categories?.find((cat: Category) => cat && cat.id === id);
                    return category ? `${category.categoryId}: ${category.name}` : null;
                })
                .filter(Boolean);
            return names.join(', ');
        };
    }, [allCategories]);

    // Check if categories are initialized
    const categoriesInitialized = useMemo(() => {
        if (!storageId) return false;
        if (categoryId) {
            return getStatus(categoryLoading, categoryError) === FormStatus.SUCCESS;
        }
        return getStatus(categoriesLoading, categoriesError) === FormStatus.SUCCESS;
    }, [storageId, categoryId, categoryLoading, categoryError, categoriesLoading, categoriesError]);

    return {
        // Single category
        category: category || null,
        categoryStatus: getStatus(categoryLoading, categoryError),
        
        // Multiple categories
        categories: allCategories || [],
        allCategories: allCategories || [],
        filteredCategories,
        categoriesStatus: getStatus(categoriesLoading, categoriesError),
        categoriesInitialized,
        
        // Filtering and sorting
        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,
        filterQuery,
        setFilterQuery,
        displayDeleted,
        setDisplayDeleted,
        
        // Actions
        reloadCategories,
        
        // Mutations (async only)
        createCategoryAsync: createCategoryMutation.mutateAsync,
        updateCategoryAsync: updateCategoryMutation.mutateAsync,
        deleteCategoryAsync: deleteCategoryMutation.mutateAsync,
        restoreCategoryAsync: restoreCategoryMutation.mutateAsync,
        setCustomInputsAsync: setCustomInputsMutation.mutateAsync,
        isCreating: createCategoryMutation.isPending,
        isUpdating: updateCategoryMutation.isPending,
        isDeleting: deleteCategoryMutation.isPending,
        isRestoring: restoreCategoryMutation.isPending,
        isSettingCustomInputs: setCustomInputsMutation.isPending,
        
        // Computed values
        findCategory,
        getCategoryNameString,
        getCategoryNamesString,
        
        // Refresh functions
        refreshCategories: refetchCategories,
        refreshCategory: refetchCategory,
    };
};

export default useCategories;
